import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@sternum/web/components';
import { ThemeMode } from '@sternum/web/components/theme/main';
import { routes } from '@sternum/web/config/routes';
import { useGeneratePathWithCurrentClient } from '@sternum/web/features/auth/hooks';
import { namespaces } from '@sternum/web/services/i18n';

import { useMenu } from '../../hooks/useMenu';
import { MenuDashboardLink } from '../MenuDashboardLink';
import { MenuItemExpandableLinkVariant } from '../MenuItemExpandableLink/MenuItemExpandableLink.types';
import { MenuItemLink } from '../MenuItemLink';
import { MenuSection } from '../MenuSection';

import { MenuContent, MenuSectionWrapper } from './Menu.styles';

const makeIcon = (type: ComponentProps<typeof Icon>['type']) => <Icon type={type} size={18} />;

export function DevicesMenu() {
  const { t } = useTranslation(namespaces.appContainerWithMenu);
  const generatePath = useGeneratePathWithCurrentClient();
  const { isFullMenuVisible, isDashboardMenuVisible } = useMenu();

  const isShowFullMenuButtonVisible = !isFullMenuVisible && !isDashboardMenuVisible;
  const menuItemVariant: MenuItemExpandableLinkVariant = isFullMenuVisible ? 'expanded' : 'collapsed';
  const tooltipThemeMode: ThemeMode = isDashboardMenuVisible ? 'Dark' : 'Light';

  return (
    <MenuContent>
      <MenuSectionWrapper $isCollapsed={isShowFullMenuButtonVisible}>
        <MenuSection>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.executiveSummary.absolute)}
            icon={makeIcon('ViewGridIcon')}
          >
            {t('menuItemSummary')}
          </MenuItemLink>
        </MenuSection>
      </MenuSectionWrapper>

      <MenuSectionWrapper $isCollapsed={isShowFullMenuButtonVisible}>
        <MenuSection title={isFullMenuVisible && t('menuSectionIncidents')}>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.attacks.absolute)}
            icon={makeIcon('ShieldCheckedIcon')}
          >
            {t('menuItemPreventAttacks')}
          </MenuItemLink>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.alerts.absolute)}
            icon={makeIcon('VirusFoundIcon')}
          >
            {t('menuItemAlerts')}
          </MenuItemLink>
        </MenuSection>
      </MenuSectionWrapper>

      <MenuSectionWrapper $isCollapsed={isShowFullMenuButtonVisible}>
        <MenuSection title={isFullMenuVisible && t('menuSectionObservability')}>
          <MenuDashboardLink
            tooltipThemeMode={tooltipThemeMode}
            icon={makeIcon('ChartIcon')}
            variant={menuItemVariant}
          />
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.fleetsView.absolute)}
            icon={makeIcon('GraphIcon')}
          >
            {t('menuItemFleetsView')}
          </MenuItemLink>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.logs.absolute)}
            icon={makeIcon('LogsIcon')}
          >
            {t('menuItemLogs')}
          </MenuItemLink>
        </MenuSection>
      </MenuSectionWrapper>

      <MenuSectionWrapper $isCollapsed={isShowFullMenuButtonVisible}>
        <MenuSection title={isFullMenuVisible && t('menuSectionManagement')} useTitlePlaceholder={!isFullMenuVisible}>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.device.absolute)}
            icon={makeIcon('TableIcon')}
          >
            {t('menuItemInventory')}
          </MenuItemLink>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.product.absolute)}
            icon={makeIcon('DisplayIcon')}
          >
            {t('menuItemProducts')}
          </MenuItemLink>
          <MenuItemLink
            tooltipThemeMode={tooltipThemeMode}
            variant={menuItemVariant}
            to={generatePath(routes.integrations.devices.absolute)}
            icon={makeIcon('ArrangeSquareIcon')}
          >
            {t('menuItemIntegrations')}
          </MenuItemLink>
        </MenuSection>
      </MenuSectionWrapper>
    </MenuContent>
  );
}
